import { useEffect, useState } from "react";
import api from "../../lib/api";
import { authenticatedDownload } from "../../lib/pdf";
import { Download, Printer, ListChecks, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import EmailSreoDialog from "./EmailSreoDialog";
import TaxInsuranceCoverageBanner from "./TaxInsuranceCoverageBanner";

const fmtMoney = (v) =>
  `$${Math.round(Number(v || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

/**
 * Schedule of Real Estate Owned (SREO) — the standard CRE lender document.
 * Columns: Property | Address | Type | Units | Purchase Date | Purchase Price |
 *          Current Value | NOI (yr) | Cap Rate | Loan Balance | Equity
 */
export default function SreoReport({ companyName = "", companyLogo = "" } = {}) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/sreo");
      setPayload(data);
    } catch (err) {
      toast.error("Failed to load SREO — " + (err.response?.status || "network error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDownload = async () => {
    if (!payload) return;
    setDownloading(true);
    try {
      await authenticatedDownload(
        "/api/reports/sreo/pdf",
        `schedule-of-real-estate-owned_${payload.as_of}.pdf`,
      );
    } catch {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const onPrint = () => window.print();

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-16 text-center text-slate-400">
        Loading Schedule of Real Estate Owned…
      </div>
    );
  }

  if (!payload || (payload.rows || []).length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-16 text-center">
        <ListChecks className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <div className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
          No properties in your portfolio yet
        </div>
        <div className="text-sm text-slate-500 mt-1">
          Add at least one property to generate your Schedule of Real Estate Owned.
        </div>
      </div>
    );
  }

  const rows = payload.rows || [];
  const t = payload.totals || {};
  // Cap rate = NOI / market_value (server returns NOI/DSCR but not cap rate) — compute locally per row.
  const capRate = (row) =>
    row.market_value && row.market_value > 0 ? row.annual_noi / row.market_value : null;
  const portfolioCap =
    t.market_value && t.market_value > 0 ? t.annual_noi / t.market_value : null;

  return (
    <div data-testid="sreo-report">
      {/* Actions (hidden when printing) */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-6 no-print flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="overline text-slate-500">As of</div>
          <div className="text-sm font-bold text-slate-900 font-mono mt-0.5">{payload.as_of}</div>
          <div className="text-xs text-slate-500 mt-1">
            {payload.properties} {payload.properties === 1 ? "property" : "properties"} · industry-standard SREO format for lenders & investors
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={load}
            data-testid="sreo-refresh-btn"
            className="btn-outline-dark flex items-center gap-2 text-sm"
            title="Refresh with latest portfolio data"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            type="button"
            onClick={onPrint}
            data-testid="sreo-print-btn"
            className="btn-outline-dark flex items-center gap-2 text-sm"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            data-testid="sreo-email-lender-btn"
            className="btn-outline-dark flex items-center gap-2 text-sm"
            title="Email the SREO PDF (optionally with current-month rent roll) to your lender contacts"
          >
            <Mail className="w-4 h-4" /> Email Lender Packet
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            data-testid="sreo-download-pdf-btn"
            className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <Download className="w-4 h-4" /> {downloading ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Printable body */}
      <div className="sreo-print-root">
        <div className="print-header hidden print:block mb-6">
          {companyLogo && (
            <img
              src={companyLogo}
              alt={companyName || "Company logo"}
              className="h-12 w-auto object-contain mb-2"
              data-testid="sreo-print-logo"
            />
          )}
          {companyName && (
            <div className="text-xs text-slate-700 uppercase tracking-widest font-bold" data-testid="sreo-print-company">
              {companyName}
            </div>
          )}
          <div className={`text-xs uppercase tracking-widest font-bold ${companyName ? "text-slate-400 mt-0.5" : "text-slate-500"}`}>
            Schedule of Real Estate Owned
          </div>
          <h1
            className="text-2xl font-bold tracking-tight text-slate-900 mt-1"
            style={{ fontFamily: "Outfit" }}
          >
            As of {payload.as_of}
          </h1>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="sreo-kpis">
          <Kpi label="Total market value" value={fmtMoney(t.market_value)} />
          <Kpi
            label="Total loan balance"
            value={fmtMoney(t.outstanding_balance)}
            bad={t.outstanding_balance > 0}
          />
          <Kpi label="Total equity" value={fmtMoney(t.equity)} good={t.equity > 0} />
          <Kpi
            label="Portfolio cap rate"
            value={portfolioCap != null ? fmtPct(portfolioCap) : "—"}
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="overline text-slate-500">Detail</div>
              <div className="text-sm font-semibold text-slate-900">
                All owned real estate
              </div>
            </div>
            <div className="text-xs text-slate-500 font-mono">
              Sorted by current value (desc)
            </div>
          </div>
          <div className="px-5 pt-4 no-print">
            <TaxInsuranceCoverageBanner />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sreo-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <Th>Property</Th>
                  <Th>Address</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Units</Th>
                  <Th>Purchase date</Th>
                  <Th className="text-right">Purchase price</Th>
                  <Th className="text-right">Current value</Th>
                  <Th className="text-right">Taxes (yr)</Th>
                  <Th className="text-right">Insurance (yr)</Th>
                  <Th className="text-right">NOI (yr)</Th>
                  <Th className="text-right">Cap rate</Th>
                  <Th className="text-right">Loan balance</Th>
                  <Th className="text-right">Equity</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.property_id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`sreo-row-${r.property_id}`}
                  >
                    <Td className="font-semibold text-slate-900 whitespace-nowrap">{r.name}</Td>
                    <Td className="text-slate-600 max-w-xs truncate" title={r.address}>
                      {r.address || "—"}
                    </Td>
                    <Td>
                      <span className="overline text-slate-500">
                        {r.property_type || "—"}
                      </span>
                    </Td>
                    <Td className="text-right font-mono">{r.units || 0}</Td>
                    <Td className="font-mono text-xs text-slate-500 whitespace-nowrap">
                      {fmtDate(r.purchase_date)}
                    </Td>
                    <Td className="text-right font-mono">{fmtMoney(r.purchase_price)}</Td>
                    <Td className="text-right font-mono font-bold text-slate-900">
                      {fmtMoney(r.market_value)}
                    </Td>
                    <Td className="text-right font-mono text-slate-700" data-testid={`sreo-taxes-${r.property_id}`}>
                      {fmtMoney(r.annual_taxes)}
                    </Td>
                    <Td className="text-right font-mono text-slate-700" data-testid={`sreo-insurance-${r.property_id}`}>
                      {fmtMoney(r.annual_insurance)}
                    </Td>
                    <Td className="text-right font-mono">{fmtMoney(r.annual_noi)}</Td>
                    <Td className="text-right font-mono">{fmtPct(capRate(r))}</Td>
                    <Td className="text-right font-mono text-amber-700">
                      {fmtMoney(r.outstanding_balance)}
                    </Td>
                    <Td
                      className={`text-right font-mono font-bold ${
                        (r.equity || 0) >= 0 ? "text-green-700" : "text-red-600"
                      }`}
                    >
                      {fmtMoney(r.equity)}
                    </Td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-900 text-white" data-testid="sreo-totals-row">
                  <Td colSpan={3} className="font-bold">
                    Portfolio totals
                  </Td>
                  <Td className="text-right font-mono font-bold">{t.units || 0}</Td>
                  <Td>—</Td>
                  <Td className="text-right font-mono font-bold">
                    {fmtMoney(t.purchase_price)}
                  </Td>
                  <Td className="text-right font-mono font-bold">
                    {fmtMoney(t.market_value)}
                  </Td>
                  <Td className="text-right font-mono font-bold" data-testid="sreo-totals-taxes">
                    {fmtMoney(t.annual_taxes)}
                  </Td>
                  <Td className="text-right font-mono font-bold" data-testid="sreo-totals-insurance">
                    {fmtMoney(t.annual_insurance)}
                  </Td>
                  <Td className="text-right font-mono font-bold">
                    {fmtMoney(t.annual_noi)}
                  </Td>
                  <Td className="text-right font-mono font-bold">
                    {portfolioCap != null ? fmtPct(portfolioCap) : "—"}
                  </Td>
                  <Td className="text-right font-mono font-bold text-amber-300">
                    {fmtMoney(t.outstanding_balance)}
                  </Td>
                  <Td className="text-right font-mono font-bold text-green-300">
                    {fmtMoney(t.equity)}
                  </Td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="mt-4 text-xs text-slate-500">
          NOI is computed from active-lease rents minus year-to-date expenses (including stored annual
          taxes + insurance). Loan balance = original principal minus recorded principal paid on the
          property's primary loan. Cap rate = annual NOI ÷ current market value.
        </div>
      </div>

      <EmailSreoDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        showRentRollToggle
        title="Email Lender Packet"
        subtitle="SREO PDF · optional current-month Rent Roll · honors your uploaded logo"
      />
    </div>
  );
}

function Kpi({ label, value, good, bad }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="overline text-slate-500">{label}</div>
      <div
        className={`mt-3 text-3xl font-bold tracking-tight font-mono ${
          good ? "text-green-600" : bad ? "text-red-600" : "text-slate-900"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 overline text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "", colSpan }) {
  return (
    <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>
      {children}
    </td>
  );
}
